import axios from 'axios';
import {
  AccommodationPublicDetailsParams,
  SearchBookableAccommodationsFilter,
  SearchResultItem,
} from '../../features/searchBookableAccommodations/searchBookableAccommodationsSlice';
import apiRequestHeaders from '../apiRequestHeaders';

export interface SearchAccommodationsResponse {
  data: SearchResultItem[];
  [key: string]: unknown;
}

export type BasicData = {
  accommodationName?: string;
  accommodationNumber?: number;
  qualityRating?: number;
  beds?: number;
  extraBeds?: number;
  area?: number;
  bedRooms?: number;
  latitude?: number;
  longitude?: number;
  mainImageUrl?: string;
  minimumOneDayPrice?: number;
  descriptionStart?: string;
  descriptionEnd?: string;
  customerFeedbackData: {
    averageAnswerValue: number;
    feedbackCount: number;
  };
};

export type LocationData = {
  municipalityName?: string;
  municipalityFullUrl?: string;
  regionName?: string;
  regionSlug?: string;
  skiResortName?: string;
  skiResortFullUrl?: string;
};

export type ImageData = {
  index: number;
  imageUrl: string;
};

export type AttributeData = {
  groupName: string;
  name: string;
  value: string;
  typeName: string;
};

export type DistanceData = {
  groupName: string;
  name: string;
  value: string;
};

export type AddOnDto = {
  addonName: string;
  units: number;
  price: number;
  amountSingleCharge: boolean;
  amountPerQuantity: boolean;
  amountPerGuest: boolean;
  durationPerNight: boolean;
  isCancellationInsurance: boolean;
  isOfficeFee: boolean;
};

export type AddOnsDto = {
  mandatoryAddons: AddOnDto[];
  optionalAddons: AddOnDto[];
};

export type AccommodationPublicDetails = {
  id: string;
  basicData?: BasicData;
  location?: LocationData;
  images?: ImageData[];
  attributes?: AttributeData[];
  distances?: DistanceData[];
  addOns?: AddOnsDto;
};

export interface AccommodationPublicDetailsResponse {
  result: AccommodationPublicDetails;
  data: unknown; // sometimes null, so keep it flexible
  message: string | null;
  isSuccess: boolean;
  headers?: Record<string, unknown>;
}

const searchService = {
  searchAccommodations: async (
    filter: SearchBookableAccommodationsFilter,
  ): Promise<SearchAccommodationsResponse> => {
    const config = {
      headers: {
        ...apiRequestHeaders.csrfHeader(),
        ...apiRequestHeaders.activeSiteHeader(),
        useLastSaleDateSetting: 'false',
      },
    };

    return axios
      .post('/search-api/searchAccommodations', filter, config)
      .then(res => res.data);
  },

  getAccommodationPublicDetails: async (
    params: AccommodationPublicDetailsParams,
  ): Promise<AccommodationPublicDetailsResponse> => {
    const config = {
      headers: {
        ...apiRequestHeaders.csrfHeader(),
        ...apiRequestHeaders.activeSiteHeader(),
      },
    };

    // Apply headers if found from params:
    if (params.includeBasicData)
      config.headers['Include-BasicData'] = params.includeBasicData;
    if (params.includeLocation)
      config.headers['Include-Location'] = params.includeLocation;
    if (params.includeImages)
      config.headers['Include-Images'] = params.includeImages;
    if (params.includeAttributes)
      config.headers['Include-Attributes'] = params.includeAttributes;
    if (params.includeDistances)
      config.headers['Include-Distances'] = params.includeDistances;
    if (params.includeAddOns)
      config.headers['Include-AddOns'] = params.includeAddOns;

    let localeParam = 'fi_FI';
    if (params.locale === 'en') {
      localeParam = 'en_EN';
    }

    return axios
      .get(
        `/search-api/getAccommodationDetails/${params.cottage}?siteId=${params.siteId}&cultureOption=${localeParam}`,
        config,
      )
      .then(res => res.data);
  },
};

export default searchService;
